import { formatCents } from "../lib/money";
import { CashDescription, CashFill } from "./Cash";

export type JarKind = "give" | "save" | "spend" | "special";

const HUE: Record<JarKind, { line: string; soft: string; deep: string }> = {
  give: { line: "var(--give)", soft: "var(--give-soft)", deep: "var(--give-deep)" },
  save: { line: "var(--save)", soft: "var(--save-soft)", deep: "var(--save-deep)" },
  spend: {
    line: "var(--spend)",
    soft: "var(--spend-soft)",
    deep: "var(--spend-deep)",
  },
  special: {
    line: "var(--special)",
    soft: "var(--special-soft)",
    deep: "var(--special-deep)",
  },
};

export function jarHue(kind: string) {
  return HUE[kind as JarKind] ?? HUE.spend;
}

/** How full a jar looks. Not linear: see .jar-level in tokens.css. */
function levelPercent(cents: number): number {
  if (cents <= 0) return 0;
  return Math.min(100, Math.max(10, Math.sqrt(cents / 2000) * 100));
}

/* A jar you could pick up.
 *
 * The jar is a drawing of an object, not a box with a label under it: a screw
 * neck narrower than the body, shoulders, a base wider than the mouth, and its
 * name written on a strip of masking tape stuck to the glass. The AMOUNT is
 * not here -- it is printed on the shelf edge below, where the shelf's own
 * front lip would otherwise cut across it.
 *
 * `showCash` is a per-child setting a parent can turn off; with it off the jar
 * keeps a fill level instead. The amount is always present on the shelf -- the
 * notes and coins are the teaching aid, not a replacement for it.
 */
export function Jar({
  kind,
  name,
  cents,
  showCash = true,
  onClick,
  open = false,
  receiving = false,
}: {
  kind: JarKind;
  name: string;
  cents: number;
  showCash?: boolean;
  onClick?: () => void;
  /** Whether this jar's detail panel is showing. Drives the lid tilt. */
  open?: boolean;
  /** Money is going into this jar right now: payday, mid-pour. Lifts it off
   *  the shelf and rings it, so a child watching the total count down can see
   *  where the money is landing without reading four figures at once. */
  receiving?: boolean;
}) {
  const hue = HUE[kind] ?? HUE.spend;
  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      className="jar"
      onClick={onClick}
      aria-label={onClick ? `${name}, ${formatCents(cents)}` : undefined}
      aria-expanded={onClick ? open : undefined}
      data-receiving={receiving ? "true" : undefined}
      style={{
        cursor: onClick ? "pointer" : "default",
        "--jar-line": hue.line,
        "--jar-soft": hue.soft,
        "--jar-deep": hue.deep,
      } as React.CSSProperties}
    >
      <div className="jar-body">
        <span className="jar-lid" aria-hidden="true" />
        <div className="jar-glass">
          <span className="jar-shine" aria-hidden="true" />
          <span className="jar-tape" aria-hidden="true">
            {name}
          </span>
          {showCash ? (
            <CashFill cents={cents} settled />
          ) : (
            <span
              className="jar-level"
              aria-hidden="true"
              style={{ height: `${levelPercent(cents)}%` }}
            />
          )}
          <CashDescription cents={cents} />
        </div>
        <span className="jar-shadow" aria-hidden="true" />
      </div>
    </Tag>
  );
}
